import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useDrawer } from '../context/DrawerContext';
import { lessonAPI, courseAPI, getLessonShareUrl } from '../services/api';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Stack,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Skeleton,
  Avatar,
  Tooltip,
  useTheme,
  alpha,
  useMediaQuery,
  Drawer,
  Divider,
  Paper,
  TextField,
  Chip,
  Alert,
} from '@mui/material';
import {
  Videocam as VideoIcon,
  Description as DocumentIcon,
  Menu as MenuIcon,
  Close as CloseIcon,
  School as SchoolIcon,
  ThumbUpOutlined as LikeIcon,
  ThumbUp as LikedIcon,
  Share as ShareIcon,
  ChatBubbleOutline as CommentIcon,
  Send as SendIcon,
  CheckCircle as CheckCircleIcon,
  Lock as LockIcon,
} from '@mui/icons-material';

import VimeoPlayer from '../components/VideoPlayer/VimeoPlayer';
import CommentItem from '../components/CommentItem/CommentItem';
import { discussionAPI } from '../services/api';
import { useThemeMode } from '../context/ThemeContext';

// Format number to K, M format
const formatCount = (count) => {
  if (!count || count === 0) return '0';
  if (count < 1000) return count.toString();
  if (count < 1000000) return (count / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
  return (count / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
};

// Curriculum Sidebar Item
const CurriculumItem = ({ lesson, isActive, onClick, isLearned }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const itemRef = React.useRef(null);
  const isVip = lesson.isVip === true || lesson.isVip === 1;
  const hasAccess = lesson.hasAccess !== false; // Default to true if not specified
  const isLocked = isVip && !hasAccess;
  
  // Scroll into view when active
  React.useEffect(() => {
    if (isActive && itemRef.current) {
      itemRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  }, [isActive]);

  const handleClick = () => {
    if (isLocked) {
      // Navigate to VIP plan page if lesson is locked
      navigate('/vip-plan');
    } else {
      onClick();
    }
  };
  
  return (
    <ListItem
      ref={itemRef}
      onClick={handleClick}
      sx={{
        py: 1,
        px: 2,
        cursor: isLocked ? 'not-allowed' : 'pointer',
        opacity: isLocked ? 0.6 : 1,
        bgcolor: isActive ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
        borderLeft: isActive ? `3px solid ${theme.palette.primary.main}` : '3px solid transparent',
        transition: 'all 0.2s ease',
        '&:hover': {
          bgcolor: isLocked 
            ? alpha(theme.palette.error.main, 0.05) 
            : alpha(theme.palette.primary.main, 0.05),
        },
      }}
    >
      <ListItemIcon sx={{ minWidth: 32 }}>
        {isLocked ? (
          <LockIcon sx={{ fontSize: 18, color: 'error.main' }} />
        ) : (lesson.isVideo || lesson.type === 'video') ? (
          <VideoIcon sx={{ fontSize: 18, color: isActive ? 'primary.main' : 'text.secondary' }} />
        ) : (
          <DocumentIcon sx={{ fontSize: 18, color: isActive ? 'primary.main' : 'text.secondary' }} />
        )}
      </ListItemIcon>
      <ListItemText
        primary={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography
              variant="body2"
              sx={{
                flex: 1,
                fontSize: '0.85rem',
                fontWeight: isActive ? 600 : 500,
                color: isActive ? 'primary.main' : 'text.primary',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {lesson.title}
            </Typography>
            {isLearned && !isLocked && (
              <CheckCircleIcon sx={{ fontSize: 18, color: 'success.main', flexShrink: 0 }} />
            )}
            {isVip && !hasAccess && (
              <Chip 
                label="VIP" 
                size="small" 
                sx={{ 
                  height: 18, 
                  fontSize: '0.65rem',
                  bgcolor: 'error.main',
                  color: 'white',
                  fontWeight: 600,
                  flexShrink: 0,
                }} 
              />
            )}
          </Box>
        }
      />
      {lesson.duration > 0 && (lesson.isVideo === 1 || lesson.isVideo === true) && !isLocked && (
        <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
          {Math.round(lesson.duration / 60)} min
        </Typography>
      )}
    </ListItem>
  );
};

export default function LessonPlay() {
  const { lessonId, courseId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const { isAuthenticated, user, loading: authLoading } = useAuth();
  const { drawerOpen, setDrawerOpen } = useDrawer();
  const { mode } = useThemeMode();

  // Close the nav drawer by default on this page
  useEffect(() => {
    setDrawerOpen(false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Update currentLessonId when URL param changes
  useEffect(() => {
    if (lessonId) {
      setCurrentLessonId(parseInt(lessonId));
    }
  }, [lessonId]);

  // Show curriculum sidebar only on desktop when nav drawer is closed
  const showSidebar = isDesktop && !drawerOpen;

  const [lessonDetail, setLessonDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentLessonId, setCurrentLessonId] = useState(parseInt(lessonId));
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [shareCount, setShareCount] = useState(0);
  const [sharing, setSharing] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [commentSubmitting, setCommentSubmitting] = useState(false);
  const [comments, setComments] = useState([]);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [vipAccessError, setVipAccessError] = useState(null);
  
  // Pagination states
  const [commentPage, setCommentPage] = useState(1);
  const [hasMoreComments, setHasMoreComments] = useState(false);
  const [loadingComments, setLoadingComments] = useState(false);
  const COMMENTS_PER_PAGE = 20;

  // Fetch curriculum only once on mount or when courseId changes
  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) return;

    const loadCurriculum = async () => {
      try {
        const [courseRes, curriculumRes] = await Promise.all([
          courseAPI.getDetail(parseInt(courseId)),
          courseAPI.getCurriculum(parseInt(courseId)).catch(() => ({ data: [] })),
        ]);

        const course = courseRes.data || courseRes;
        
        // Use curriculum from new endpoint, falling back to other sources if needed
        let curriculumResData = curriculumRes.data || curriculumRes;
        
        // NEW API STRUCTURE: { success: true, data: { curriculum: [ [lesson, lesson], [lesson, lesson] ] } }
        let rawCurriculum = [];
        if (curriculumResData.curriculum && Array.isArray(curriculumResData.curriculum)) {
             rawCurriculum = curriculumResData.curriculum;
        } else if (Array.isArray(curriculumResData)) {
             rawCurriculum = curriculumResData;
        } else if (course.curriculum) {
             rawCurriculum = course.curriculum || [];
        }

        // Map the API response to the UI expected format
        const formattedCurriculum = rawCurriculum.map((sectionLessons, index) => {
            // Check if sectionLessons is an array (new format) or object (old format)
            const lessons = Array.isArray(sectionLessons) ? sectionLessons : (sectionLessons.lessons || []);
            const title = Array.isArray(sectionLessons) ? `Day ${index + 1}` : sectionLessons.title;

            // Calculate total duration for the section
            const durationSecs = lessons.reduce((acc, l) => acc + (l.duration || 0), 0) || 0;
            const hours = Math.floor(durationSecs / 3600);
            const minutes = Math.floor((durationSecs % 3600) / 60);
            const durationStr = hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;

            return {
                day: index + 1, // Use index + 1 as "Day" or Section number
                title: title,
                lessons: lessons.map(l => ({
                    ...l,
                    formattedDuration: `${Math.floor((l.duration || 0) / 60)}:${((l.duration || 0) % 60).toString().padStart(2, '0')}`
                })),
                lessonsCount: lessons.length || 0,
                totalDuration: durationStr
            };
        });

        setLessonDetail(prev => ({
          ...prev,
          course: course,
          curriculum: formattedCurriculum,
        }));
      } catch (err) {
        console.error('Failed to load course curriculum:', err);
      }
    };

    loadCurriculum();
  }, [courseId, isAuthenticated, authLoading, user?.id]);

  // Fetch lesson details when currentLessonId changes
  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    const loadLesson = async () => {
      setLoading(true);
      setLiked(false);
      setLikeCount(0);
      setShareCount(0);
      setVipAccessError(null);
      try {
        const lessonData = await lessonAPI.getDetail(currentLessonId);

        // Check if lesson requires VIP access
        if (lessonData.success === false && lessonData.requiresSubscription === true) {
          setVipAccessError('ဤသင်ခန်းစာသည် VIP သီးသန့်ဖြစ်ပါသည်။ ဆက်လက်လေ့လာရန် VIP အဖွဲ့ဝင်ဝင်ရောက်ရန် လိုအပ်ပါသည်။');
          setLoading(false);
          return;
        }

        const lesson = lessonData.data?.lesson || lessonData.lesson || lessonData;
        
        setLessonDetail(prev => ({
          ...prev,
          lesson: lesson,
        }));

        // Mark lesson as learned when user views it (for both video and document lessons)
        if (isAuthenticated && user?.id && lesson.learned !== 1) {
          try {
            await lessonAPI.markLearned(currentLessonId);
            // Update learned status in state
            setLessonDetail(prev => prev ? {
              ...prev,
              lesson: { ...prev.lesson, learned: 1 },
              curriculum: prev.curriculum?.map(cat => ({
                ...cat,
                lessons: cat.lessons?.map(l => l.id === currentLessonId ? { ...l, learned: 1 } : l)
              }))
            } : null);
          } catch (err) {
            console.error('Failed to mark lesson as learned:', err);
            // Don't block UI if this fails
          }
        }

        // Fetch lesson comments for video lessons using polymorphic lesson target
        if ((lesson.isVideo === 1 || lesson.isVideo === true)) {
          try {
            // Set like status and counts from lesson detail data
            setLiked(lesson.isLiked === 1);
            setLikeCount(lesson.likeCount || 0);
            setShareCount(lesson.shareCount || 0);

            // Fetch comments - initial load
            setCommentPage(1);
            setLoadingComments(true);
            try {
                const commentsResponse = await discussionAPI.getComments(
                  { lessonId: lesson.id, targetType: 'lesson', targetId: lesson.id },
                  1,
                  COMMENTS_PER_PAGE,
                  user?.id || null
                );
                
                let commentsList = [];
                let pagination = {};
    
                if (Array.isArray(commentsResponse.data)) {
                    commentsList = commentsResponse.data;
                    pagination = commentsResponse.meta || {};
                } else if (commentsResponse.data && Array.isArray(commentsResponse.data.comments)) {
                    commentsList = commentsResponse.data.comments;
                    pagination = commentsResponse.data.pagination || {};
                }
                
                setComments(commentsList);
                
                // Determine if there are more comments
                if (pagination.currentPage && pagination.lastPage) {
                    setHasMoreComments(pagination.currentPage < pagination.lastPage);
                } else {
                    const totalComments = pagination.totalComments || pagination.total || 0;
                    setHasMoreComments(commentsList.length < totalComments && commentsList.length > 0);
                }

                // Update total count
                const totalComments = pagination.totalComments || pagination.totalComments || pagination.total;
                if (totalComments !== undefined) {
                   setLessonDetail(prev => ({
                     ...prev,
                     lesson: { ...prev.lesson, comments: totalComments, commentCount: totalComments }
                   }));
                }
            } catch (cErr) {
                 setComments([]);
                 setHasMoreComments(false);
            } finally {
                setLoadingComments(false);
            }
          } catch (err) {
            // Only log if it's not a 404
            if (err.status !== 404 && !err.message?.includes('404')) {
              console.error('Failed to fetch comments:', err);
            }
            setComments([]);
          }
        } else {
          // Non-video lesson: keep social counts from lesson payload
          setComments([]);
          setLiked(false);
          setLikeCount(lesson.likeCount || 0);
          setShareCount(0);
        }
      } catch (err) {
        console.error('Failed to load lesson detail:', err);
        // Check if error is due to VIP access (403 or API flag)
        if (err.status === 403 || err.response?.data?.requiresSubscription === true) {
          setVipAccessError('ဤသင်ခန်းစာသည် VIP သီးသန့်ဖြစ်ပါသည်။ ဆက်လက်လေ့လာရန် VIP အဖွဲ့ဝင်ဝင်ရောက်ရန် လိုအပ်ပါသည်။');
        } else {
          setVipAccessError(err.message || 'Failed to load lesson. Please try again.');
        }
        setLessonDetail(prev => ({ ...prev, lesson: null }));
      } finally {
        setLoading(false);
      }
    };

    loadLesson();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentLessonId, courseId, isAuthenticated, authLoading, navigate, user?.id]);

  const handleLessonClick = (newLessonId) => {
    // Check if the lesson is VIP and user doesn't have access
    const lesson = lessonDetail?.curriculum
      ?.flatMap(day => day.lessons || [])
      .find(l => l.id === newLessonId);
    
    if (lesson && lesson.isVip && !lesson.hasAccess) {
      // Navigate to VIP plan page
      navigate('/vip-plan');
      return;
    }
    
    // Update URL to reflect new lesson ID
    navigate(`/course/${courseId}/lesson/${newLessonId}`);
    
    if (!isDesktop) setMobileSidebarOpen(false);
  };

  // Comment tree manipulation helpers
  const updateCommentInTree = (list, commentId, updater) => {
    return list.map((c) => {
      if (c.time === commentId) return updater(c);
      if (c.child?.length) return { ...c, child: updateCommentInTree(c.child, commentId, updater) };
      if (c.replies?.length) return { ...c, replies: updateCommentInTree(c.replies, commentId, updater) };
      return c;
    });
  };

  const removeCommentFromTree = (list, commentId) => {
    return list.filter((c) => c.time !== commentId).map((c) => {
      if (c.child?.length) return { ...c, child: removeCommentFromTree(c.child, commentId) };
      if (c.replies?.length) return { ...c, replies: removeCommentFromTree(c.replies, commentId) };
      return c;
    });
  };

  const addReplyToTree = (list, parentId, newComment) => {
    return list.map((c) => {
      if (c.time === parentId) {
        if (c.child) return { ...c, child: [...c.child, newComment] };
        return { ...c, replies: [...(c.replies || []), newComment] };
      }
      if (c.child?.length) return { ...c, child: addReplyToTree(c.child, parentId, newComment) };
      if (c.replies?.length) return { ...c, replies: addReplyToTree(c.replies, parentId, newComment) };
      return c;
    });
  };

  // Comment handlers
  const handleLikeComment = async (commentTime, isLiked) => {
    if (!isAuthenticated) { navigate('/login'); return; }
    
    // Optimistic update
    setComments((prev) => updateCommentInTree(prev, commentTime, (c) => ({
      ...c, 
      likes: isLiked ? c.likes - 1 : c.likes + 1, 
      isLiked: isLiked ? 0 : 1,
    })));

    try {
      // The API now expects commentId (time), not postId
      const result = await discussionAPI.likeComment(commentTime);
      if (result.success) {
        setComments((prev) => updateCommentInTree(prev, commentTime, (c) => ({
          ...c,
          likes: result.likesCount ?? c.likes,
          isLiked: result.isLiked ? 1 : 0,
        })));
      }
    } catch (err) {
      console.error('Like comment error:', err);
      // Revert optimistic update on error
      setComments((prev) => updateCommentInTree(prev, commentTime, (c) => ({
        ...c, 
        likes: isLiked ? c.likes + 1 : c.likes - 1, 
        isLiked: isLiked ? 1 : 0,
      })));
    }
  };

  const handleDeleteComment = async (pId, commentId) => {
    try {
      const result = await discussionAPI.deleteComment(commentId);
      setComments((prev) => removeCommentFromTree(prev, commentId));
      if (result.data?.commentsCount !== undefined && lessonDetail) {
        setLessonDetail(prev => ({
          ...prev,
          lesson: { ...prev.lesson, comments: result.data.commentsCount, commentCount: result.data.commentsCount }
        }));
      }
    } catch (err) {
      console.error('Delete comment error:', err);
    }
  };

  const handleUpdateComment = async (pId, commentId, body) => {
    if (!isAuthenticated) { navigate('/login'); return; }
    try {
      const result = await discussionAPI.updateComment(commentId, body);
      if (result.success) {
        setComments((prev) => updateCommentInTree(prev, commentId, (c) => ({
          ...c,
          body: body,
        })));
      }
    } catch (err) {
      console.error('Update comment error:', err);
    }
  };

  const handleReplySubmit = async (parentId, body) => {
    if (!isAuthenticated) { navigate('/login'); return; }
    if (!lessonDetail?.lesson?.id) return;
    
    // Create optimistic reply immediately
    const optimisticReply = {
      id: 0,
      lessonId: lessonDetail.lesson.id,
      targetType: 'lesson',
      targetId: lessonDetail.lesson.id,
      writerId: user?.id || '',
      body: body,
      time: Date.now(), // Temporary timestamp
      parent: parentId,
      likes: 0,
      writerName: user?.name || user?.learnerName || 'You',
      writerImage: user?.image || user?.learnerImage || 'https://www.calamuseducation.com/uploads/profiles/calamus.png',
      isLiked: 0,
      replies: [],
    };
    
    // Add optimistic reply to tree immediately
    setComments((prev) => addReplyToTree(prev, parentId, optimisticReply));
    if (lessonDetail) {
      setLessonDetail(prev => ({
        ...prev,
        lesson: {
          ...prev.lesson,
          comments: (prev.lesson.commentCount ?? prev.lesson.comments ?? 0) + 1,
          commentCount: (prev.lesson.commentCount ?? prev.lesson.comments ?? 0) + 1,
        }
      }));
    }
    
    try {
      const result = await lessonAPI.comment({
        lessonId: lessonDetail.lesson.id,
        body,
        parent: parentId,
      });
      if (result.data?.comment) {
        // Replace optimistic reply with real one from server
        setComments((prev) => {
          // First remove optimistic reply
          const withoutOptimistic = prev.map((c) => {
            if (c.time === parentId) {
              return {
                ...c,
                child: c.child?.filter((r) => r.time !== optimisticReply.time) || [],
                replies: c.replies?.filter((r) => r.time !== optimisticReply.time) || []
              };
            }
            if (c.child?.length || c.replies?.length) {
              return {
                ...c,
                child: c.child ? removeCommentFromTree(c.child, optimisticReply.time) : undefined,
                replies: c.replies ? removeCommentFromTree(c.replies, optimisticReply.time) : undefined
              };
            }
            return c;
          });
          // Then add real reply
          return addReplyToTree(withoutOptimistic, parentId, result.data.comment);
        });
      }
    } catch (err) {
      console.error('Reply submit error:', err);
      // Revert optimistic update on error
      setComments((prev) => {
        return prev.map((c) => {
          if (c.time === parentId) {
            return {
              ...c,
              child: c.child?.filter((r) => r.time !== optimisticReply.time) || [],
              replies: c.replies?.filter((r) => r.time !== optimisticReply.time) || []
            };
          }
          if (c.child?.length || c.replies?.length) {
            return {
              ...c,
              child: c.child ? removeCommentFromTree(c.child, optimisticReply.time) : undefined,
              replies: c.replies ? removeCommentFromTree(c.replies, optimisticReply.time) : undefined
            };
          }
          return c;
        });
      });
      if (lessonDetail) {
        setLessonDetail(prev => ({
          ...prev,
          lesson: {
            ...prev.lesson,
            comments: Math.max(0, (prev.lesson.commentCount ?? prev.lesson.comments ?? 0) - 1),
            commentCount: Math.max(0, (prev.lesson.commentCount ?? prev.lesson.comments ?? 0) - 1),
          }
        }));
      }
    }
  };

  const handleSubmitComment = async () => {
    const body = commentText.trim();
    if (!body) return;
    if (!isAuthenticated) { navigate('/login'); return; }
    if (!lessonDetail?.lesson?.id) return;
    
    // Clear input immediately
    const commentTextToSubmit = body;
    setCommentText('');
    setCommentSubmitting(true);
    
    // Optimistic update - add comment immediately
    const optimisticComment = {
      id: 0,
      lessonId: lessonDetail.lesson.id,
      targetType: 'lesson',
      targetId: lessonDetail.lesson.id,
      writerId: user?.id || '',
      body: commentTextToSubmit,
      time: Date.now(), // Temporary timestamp
      parent: 0,
      likes: 0,
      userName: user?.name || user?.learnerName || 'You',
      userImage: user?.image || user?.learnerImage || 'https://www.calamuseducation.com/uploads/profiles/calamus.png',
      isLiked: 0,
      child: [],
    };
    
    setComments((prev) => [optimisticComment, ...prev]);
    if (lessonDetail) {
      setLessonDetail(prev => ({
        ...prev,
        lesson: {
          ...prev.lesson,
          comments: (prev.lesson.commentCount ?? prev.lesson.comments ?? 0) + 1,
          commentCount: (prev.lesson.commentCount ?? prev.lesson.comments ?? 0) + 1,
        }
      }));
    }
    
    try {
      const result = await lessonAPI.comment({
        lessonId: lessonDetail.lesson.id,
        body: commentTextToSubmit,
      });
      if (result.data?.comment) {
        // Replace optimistic comment with real one from server
        setComments((prev) => {
          const filtered = prev.filter((c) => c.time !== optimisticComment.time);
          return [result.data.comment, ...filtered];
        });
      }
    } catch (err) {
      console.error('Submit comment error:', err);
      // Revert optimistic update on error
      setComments((prev) => prev.filter((c) => c.time !== optimisticComment.time));
      if (lessonDetail) {
        setLessonDetail(prev => ({
          ...prev,
          lesson: {
            ...prev.lesson,
            comments: Math.max(0, (prev.lesson.commentCount ?? prev.lesson.comments ?? 0) - 1),
            commentCount: Math.max(0, (prev.lesson.commentCount ?? prev.lesson.comments ?? 0) - 1),
          }
        }));
      }
      setCommentText(commentTextToSubmit); // Restore text
    } finally {
      setCommentSubmitting(false);
    }
  };

  const handleLike = async () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    if (!lessonDetail?.lesson?.id) return;
    
    // Optimistic update
    const newLikedState = !liked;
    setLiked(newLikedState);
    setLikeCount(newLikedState ? likeCount + 1 : likeCount - 1);
    
    try {
      const result = await lessonAPI.like(lessonDetail.lesson.id);
      if (result.success) {
        const nextCount = result.data?.count;
        const nextLiked = result.data?.isLiked;
        if (nextCount !== undefined) setLikeCount(nextCount);
        if (nextLiked !== undefined) setLiked(Boolean(nextLiked));
      }
    } catch (err) {
      // Revert on error
      setLiked(!newLikedState);
      setLikeCount(newLikedState ? likeCount - 1 : likeCount + 1);
      console.error('Like error:', err);
    }
  };
  
  const handleShare = async () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    if (!lessonDetail?.lesson?.id) return;
    
    setSharing(true);
    try {
      const result = await lessonAPI.share(lessonDetail.lesson.id);
      if (result.success) {
        if (result.data?.count !== undefined) {
          setShareCount(result.data.count);
        }

        const shareUrl = getLessonShareUrl(lessonDetail.lesson.id, courseId);
        
        // Also try native share if available
        if (navigator.share) {
          try {
            await navigator.share({
              title: lessonDetail?.lesson?.title,
              url: shareUrl,
            });
          } catch (shareErr) {
            // User cancelled or error - that's okay
            if (shareErr.name !== 'AbortError') {
              console.error('Share error:', shareErr);
            }
          }
        }
      }
    } catch (err) {
      console.error('Share error:', err);
    } finally {
      setSharing(false);
    }
  };

  const handleLoadMoreComments = async () => {
    if (loadingComments || !hasMoreComments || !lessonDetail?.lesson?.id) return;
    
    const nextPage = commentPage + 1;
    setLoadingComments(true);
    
    try {
      const commentsResponse = await discussionAPI.getComments(
        {
          lessonId: lessonDetail.lesson.id,
          targetType: 'lesson',
          targetId: lessonDetail.lesson.id,
        },
        nextPage,
        COMMENTS_PER_PAGE,
        user?.id || null
      );
      
      let commentsList = [];
      let pagination = {};

      if (Array.isArray(commentsResponse.data)) {
          commentsList = commentsResponse.data;
          pagination = commentsResponse.meta || {};
      } else if (commentsResponse.data && Array.isArray(commentsResponse.data.comments)) {
          commentsList = commentsResponse.data.comments;
          pagination = commentsResponse.data.pagination || {};
      }
      
      if (commentsList.length > 0) {
        setComments(prev => [...prev, ...commentsList]);
        setCommentPage(nextPage);
        
        // Check if we reached the end
        if (pagination.currentPage && pagination.lastPage) {
            setHasMoreComments(pagination.currentPage < pagination.lastPage);
        } else {
            const totalComments = pagination.totalComments || pagination.total || 0;
            const currentTotal = comments.length + commentsList.length;
            setHasMoreComments(currentTotal < totalComments);
        }
      } else {
        setHasMoreComments(false);
      }
    } catch (err) {
      console.error('Failed to load more comments:', err);
    } finally {
      setLoadingComments(false);
    }
  };

  const handleBack = () => navigate(`/course/${courseId}`);

  // Loading skeleton — matches WatchVideo layout
  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', p: { xs: 0, sm: 2, lg: 3 } }}>
        <Box sx={{ display: 'flex', gap: 3, maxWidth: 1800, mx: 'auto' }}>
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="rectangular" sx={{ width: '100%', aspectRatio: '16/9', borderRadius: { xs: 0, lg: 2 } }} />
            <Box sx={{ p: 2 }}>
              <Skeleton variant="text" width="80%" height={28} />
              <Skeleton variant="text" width="40%" height={20} />
            </Box>
          </Box>
          {isDesktop && (
            <Box sx={{ width: 350 }}>
              {[1, 2, 3, 4, 5].map((i) => (
                <Box key={i} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                  <Skeleton variant="circular" width={18} height={18} />
                  <Skeleton variant="text" width="80%" />
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Box>
    );
  }

  // Error state
  if (!lessonDetail && !vipAccessError) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: 'background.default',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <SchoolIcon sx={{ fontSize: 80, color: 'grey.300' }} />
        <Typography variant="h5" fontWeight={600}>
          Lesson not found
        </Typography>
        <Button variant="contained" onClick={handleBack}>
          Back to Course
        </Button>
      </Box>
    );
  }

  // If VIP access error, show beautiful access denied UI
  if (vipAccessError && !lessonDetail) {
    return (
      <Box 
        sx={{ 
          minHeight: '100vh', 
          bgcolor: 'background.default', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          p: 3 
        }}
      >
        <Paper
          elevation={0}
          sx={{
            maxWidth: 500,
            width: '100%',
            p: 4,
            textAlign: 'center',
            borderRadius: 4,
            boxShadow: '0 10px 40px rgba(0,0,0,0.04)',
            border: '1px solid',
            borderColor: alpha(theme.palette.divider, 0.1),
            bgcolor: 'background.paper'
          }}
        >
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              bgcolor: alpha(theme.palette.error.main, 0.1),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 3
            }}
          >
            <LockIcon sx={{ fontSize: 40, color: 'error.main' }} />
          </Box>
          
          <Typography variant="h5" fontWeight={800} gutterBottom sx={{ color: 'text.primary' }}>
            VIP Content Locked
          </Typography>
          
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.6 }}>
            ဤသင်ခန်းစာသည် VIP သီးသန့်ဖြစ်ပါသည်။ ဆက်လက်လေ့လာရန် VIP အဖွဲ့ဝင်ဝင်ရောက်ရန် လိုအပ်ပါသည်။
          </Typography>
          
          <Stack spacing={2}>
            <Button
              variant="contained"
              fullWidth
              size="large"
              onClick={() => navigate('/vip-plan')}
              sx={{
                py: 1.5,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '1rem',
                boxShadow: `0 8px 20px ${alpha(theme.palette.primary.main, 0.3)}`,
                '&:hover': {
                  boxShadow: `0 12px 25px ${alpha(theme.palette.primary.main, 0.4)}`,
                }
              }}
            >
              VIP အဖွဲ့ဝင်ဝင်မည်
            </Button>
            
            <Button
              variant="outlined"
              fullWidth
              size="large"
              onClick={handleBack}
              sx={{
                py: 1.5,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                borderColor: alpha(theme.palette.divider, 0.5),
                color: 'text.secondary',
                '&:hover': {
                  borderColor: 'text.primary',
                  bgcolor: 'transparent',
                  color: 'text.primary'
                }
              }}
            >
              သင်တန်းသို့ ပြန်သွားမည်
            </Button>
          </Stack>
        </Paper>
      </Box>
    );
  }

  const { lesson, course, curriculum } = lessonDetail || { lesson: null, course: null, curriculum: [] };
  const courseTitle = course?.title || '';
  const courseInstructorImage = course?.instructorImage || undefined;
  const courseInstructorName = course?.instructorName || '';

  // Curriculum sidebar content
  const sidebarContent = (
    <Paper
      sx={{
        height: 'auto',
        borderRadius: 2,
        overflow: 'hidden',
        bgcolor: 'background.paper',
      }}
    >
      <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'action.hover' }}>
        <Typography variant="h6" fontWeight={700}>
          Course Curriculum
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {courseTitle}
        </Typography>
      </Box>
      <List disablePadding sx={{ maxHeight: '70vh', overflowY: 'auto' }}>
        {Array.isArray(curriculum) && curriculum.map((day) => (
          <Box key={day.day}>
            <Box sx={{ px: 2, py: 1.5, bgcolor: 'action.hover', borderBottom: '1px solid', borderColor: 'divider' }}>
              <Typography variant="subtitle2" fontWeight={600} fontSize="0.8rem">
                {`Day ${day.day} • ${day.lessonsCount} lectures • ${day.totalDuration}`}
              </Typography>
            </Box>
            {day.lessons?.map((lsn) => (
              <CurriculumItem
                key={lsn.id}
                lesson={lsn}
                isActive={lsn.id === currentLessonId}
                isLearned={lsn.learned === 1}
                onClick={() => handleLessonClick(lsn.id)}
              />
            ))}
          </Box>
        ))}
      </List>
    </Paper>
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Main Layout Container — same as WatchVideo */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: showSidebar ? 'row' : 'column',
          gap: showSidebar ? 3 : 0,
          maxWidth: 1800,
          mx: 'auto',
          p: { xs: 0, sm: 2, md: 3 },
        }}
      >
        {/* Left Column — Video Player & Info & Comments OR Document Viewer */}
        <Box sx={{ flex: 1, minWidth: 0, maxWidth: showSidebar ? 'calc(100% - 374px)' : '100%' }}>
          {/* VIP Access Error Message (if lessonDetail exists but lesson is VIP) */}
          {vipAccessError && lessonDetail && (
            <Alert 
              severity="warning" 
              action={
                <Button 
                  color="inherit" 
                  size="small" 
                  onClick={() => navigate('/vip-plan')}
                >
                  Subscribe
                </Button>
              }
              sx={{ mb: 2 }}
            >
              {vipAccessError}
            </Alert>
          )}
          
          {/* Video Lesson: Show Video Player & Info & Comments */}
          {lesson && (lesson.isVideo === 1 || lesson.isVideo === true) ? (
            <>
              {/* Video Player */}
              <VimeoPlayer
                src={lesson.vimeo || ''}
                title={lesson.title}
                onBack={handleBack}
              />

              {/* Video Info Section — matches WatchVideo */}
              <Box sx={{ p: { xs: 2, lg: 0 }, pt: { lg: 2 } }}>
                {/* Title */}
                <Typography
                  variant="h6"
                  fontWeight={600}
                  sx={{
                    fontSize: { xs: '1rem', sm: '1.125rem' },
                    lineHeight: 1.4,
                    mb: 1.5,
                  }}
                >
                  {lesson.title}
                </Typography>

                {/* Channel Info & Actions Row — same as WatchVideo */}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    justifyContent: 'space-between',
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    gap: 2,
                    pb: 2,
                    boxShadow: '0 1px 0 rgba(0,0,0,0.05)',
                  }}
                >
                  {/* Instructor Info */}
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Avatar
                      src={courseInstructorImage}
                      sx={{
                        width: 40,
                        height: 40,
                        bgcolor: 'primary.main',
                        fontSize: '1rem',
                      }}
                    >
                      {courseInstructorName?.charAt(0) || 'C'}
                    </Avatar>
                    <Box>
                      <Typography fontWeight={600} sx={{ fontSize: '0.9rem' }}>
                        {courseInstructorName || courseTitle}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {lesson.viewCount ? `${formatCount(lesson.viewCount)} views` : ''}{lesson.viewCount && lesson.duration ? ' • ' : ''}{lesson.duration ? `${Math.round(lesson.duration / 60)} min` : ''}
                      </Typography>
                    </Box>
                  </Stack>

                  {/* Action Buttons */}
                  <Stack direction="row" spacing={1} alignItems="center">
                    {/* Like Button */}
                    <Tooltip title="Like" arrow>
                      <Button
                        onClick={handleLike}
                        sx={{
                          minWidth: 'auto',
                          px: 2,
                          py: 0.75,
                          bgcolor: liked ? alpha(theme.palette.primary.main, 0.1) : alpha('#000', 0.05),
                          color: liked ? 'primary.main' : 'text.primary',
                          borderRadius: 5,
                          textTransform: 'none',
                          '&:hover': { bgcolor: alpha('#000', 0.1) },
                        }}
                        startIcon={liked ? <LikedIcon /> : <LikeIcon />}
                      >
                        {formatCount(likeCount)}
                      </Button>
                    </Tooltip>

                    {/* Share Button */}
                    <Tooltip title="Share" arrow>
                      <Button
                        onClick={handleShare}
                        disabled={sharing}
                        sx={{
                          minWidth: 'auto',
                          px: 2,
                          py: 0.75,
                          bgcolor: alpha('#000', 0.05),
                          color: 'text.primary',
                          borderRadius: 5,
                          textTransform: 'none',
                          '&:hover': { bgcolor: alpha('#000', 0.1) },
                        }}
                        startIcon={<ShareIcon sx={{ fontSize: 20 }} />}
                      >
                        Share{shareCount > 0 && ` (${formatCount(shareCount)})`}
                      </Button>
                    </Tooltip>

                    {/* Curriculum toggle (mobile) */}
                    {!isDesktop && (
                      <Tooltip title="Curriculum" arrow>
                        <IconButton
                          onClick={() => setMobileSidebarOpen(true)}
                          sx={{
                            bgcolor: alpha('#000', 0.05),
                            '&:hover': { bgcolor: alpha('#000', 0.1) },
                          }}
                        >
                          <MenuIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                </Box>

                {/* Lesson description (collapsible, if exists) */}
                {lesson.description && (
                  <Box
                    sx={{
                      mt: 2,
                      p: 2,
                      bgcolor: alpha('#000', 0.03),
                      borderRadius: 2,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                      {lesson.description}
                    </Typography>
                  </Box>
                )}

                {/* Comments Section — Full CRUD functionality */}
                {lesson && (
                  <Paper 
                    elevation={0}
                    sx={{ 
                      mt: 2, 
                      borderRadius: 4,
                      border: 'none',
                      boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                      p: 2,
                    }}
                  >
                    <Typography variant="h6" fontWeight={600} gutterBottom>
                      Comments ({lesson.commentCount ?? lesson.comments ?? comments.length})
                    </Typography>
                    
                    {/* Comment Input */}
                    <Stack direction="row" spacing={1.5} sx={{ mb: 3 }}>
                      <Avatar
                        src={user?.image || user?.learner_image || 'https://www.calamuseducation.com/uploads/placeholder.png'}
                        sx={{ width: 36, height: 36 }}
                      />
                      <TextField
                        size="small"
                        placeholder="Write a comment..."
                        fullWidth
                        multiline
                        maxRows={4}
                        value={commentText}
                        onChange={(e) => setCommentText(e.target.value)}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '20px',
                            bgcolor: mode === 'light' ? 'grey.100' : alpha(theme.palette.common.white, 0.05),
                            '& fieldset': { border: 'none' },
                            color: 'text.primary',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              bgcolor: mode === 'light' ? 'grey.200' : alpha(theme.palette.common.white, 0.08),
                            },
                            '&.Mui-focused': {
                              bgcolor: mode === 'light' ? '#fff' : alpha(theme.palette.common.white, 0.1),
                              boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.3)}`,
                            }
                          },
                        }}
                      />
                      <IconButton 
                        color="primary" 
                        onClick={handleSubmitComment}
                        disabled={!commentText.trim() || commentSubmitting}
                      >
                        <SendIcon />
                      </IconButton>
                    </Stack>
                    
                    <Divider sx={{ mb: 2 }} />
                    
                    {/* Comments List */}
                    {comments.length === 0 ? (
                      <Box sx={{ textAlign: 'center', py: 4 }}>
                        <CommentIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                        <Typography variant="body1" color="text.secondary">
                          No comments yet
                        </Typography>
                        <Typography variant="body2" color="text.disabled">
                          Be the first to comment!
                        </Typography>
                      </Box>
                    ) : (
                      <>
                        {comments.map((comment) => (
                          <CommentItem
                            key={comment.id || comment.time}
                            postId={lesson.id}
                            comment={comment}
                            currentUserId={user?.id}
                            onLikeComment={handleLikeComment}
                            onDeleteComment={isAuthenticated ? handleDeleteComment : null}
                            onUpdateComment={isAuthenticated ? handleUpdateComment : null}
                            onReplySubmit={isAuthenticated ? handleReplySubmit : null}
                            isAuthenticated={!!isAuthenticated}
                          />
                        ))}
                        
                        {/* Load More Button */}
                        {hasMoreComments && (
                          <Box sx={{ textAlign: 'center', mt: 2, mb: 1 }}>
                            <Button 
                              variant="text" 
                              onClick={handleLoadMoreComments}
                              disabled={loadingComments}
                              sx={{ textTransform: 'none', color: 'text.secondary' }}
                            >
                              {loadingComments ? 'Loading...' : 'Load more comments'}
                            </Button>
                          </Box>
                        )}
                      </>
                    )}
                  </Paper>
                )}
              </Box>
            </>
          ) : (
            /* Document Lesson: Show ONLY Document Viewer (full column) */
            <Box
              sx={{
                width: '100%',
                bgcolor: 'background.paper',
                borderRadius: { xs: 0, lg: 2 },
                overflow: 'hidden',
                border: 'none',
                boxShadow: { xs: 'none', lg: '0 2px 12px rgba(0,0,0,0.06)' },
                position: 'relative',
              }}
            >
              {/* Curriculum toggle button for mobile - positioned at top right */}
              {!isDesktop && (
                <Tooltip title="Curriculum" arrow>
                  <IconButton
                    onClick={() => setMobileSidebarOpen(true)}
                    sx={{
                      position: 'absolute',
                      top: 12,
                      right: 12,
                      zIndex: 10,
                      bgcolor: 'rgba(0,0,0,0.5)',
                      color: 'white',
                      '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                    }}
                  >
                    <MenuIcon />
                  </IconButton>
                </Tooltip>
              )}
              
              {lesson && lesson.documentUrl ? (
                <Box
                  component="iframe"
                  src={lesson.documentUrl}
                  sx={{
                    width: '100%',
                    border: 'none',
                    display: 'block',
                  }}
                  title={lesson.title}
                  allowFullScreen
                  onLoad={(e) => {
                    // Resize iframe to match content height
                    try {
                      const iframe = e.target;
                      const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
                      if (iframeDoc && iframeDoc.body) {
                        const height = Math.max(
                          iframeDoc.body.scrollHeight,
                          iframeDoc.body.offsetHeight,
                          iframeDoc.documentElement.clientHeight,
                          iframeDoc.documentElement.scrollHeight,
                          iframeDoc.documentElement.offsetHeight
                        );
                        iframe.style.height = height + 'px';
                      }
                    } catch (err) {
                      // Cross-origin restriction - set a reasonable default height
                      e.target.style.height = '1200px';
                    }
                  }}
                />
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '400px',
                    p: 4,
                    textAlign: 'center',
                  }}
                >
                  <DocumentIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    Document not available
                  </Typography>
                  <Typography variant="body2" color="text.disabled">
                    The lesson document could not be loaded.
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {/* Mobile curriculum is accessible via the Curriculum drawer button */}
        </Box>

        {/* Right Sidebar — Curriculum (Desktop only, hidden when drawer is open) */}
        {showSidebar && (
          <Box
            sx={{
              width: 350,
              minWidth: 350,
              maxWidth: 350,
            }}
          >
            {sidebarContent}
          </Box>
        )}
      </Box>

      {/* Mobile Curriculum Drawer */}
      {!isDesktop && (
        <Drawer
          anchor="right"
          open={mobileSidebarOpen}
          onClose={() => setMobileSidebarOpen(false)}
          PaperProps={{ sx: { width: 300 } }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
            <IconButton onClick={() => setMobileSidebarOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
          {sidebarContent}
        </Drawer>
      )}
    </Box>
  );
}
